// AutomationFilters, not the model namespaces: `parseCollectorsSearch` is the route's
// `validateSearch`, which stays in the entry chunk (docs/code-splitting.md).
import {
  type Automation,
  type AutomationCollector,
  AutomationFilters,
  type AutomationWire,
} from '~api';

/**
 * @desc The Collectors page's URL-held state: the list filters and, as `collector`, the id whose
 * detail drawer is open — so a collector can be linked to directly (the Runs page does).
 */
export type CollectorsSearch = {
  kind?: AutomationCollector.Kind;
  status?: Automation.EntityStatus;
  collector?: string;
};

const OBJECT_ID = /^[0-9a-f]{24}$/i;
const kinds = Object.values(AutomationFilters.collectorKinds) as readonly string[];
const statuses = Object.values(AutomationFilters.entityStatuses) as readonly string[];

/** @desc Lenient: an unknown or malformed value is dropped rather than refusing the page. */
export function parseCollectorsSearch(search: Record<string, unknown>): CollectorsSearch {
  const kind =
    typeof search.kind === 'string' && kinds.includes(search.kind)
      ? (search.kind as AutomationCollector.Kind)
      : undefined;
  const status =
    typeof search.status === 'string' && statuses.includes(search.status)
      ? (search.status as Automation.EntityStatus)
      : undefined;
  const collector =
    typeof search.collector === 'string' && OBJECT_ID.test(search.collector)
      ? search.collector
      : undefined;
  return {
    ...(kind ? { kind } : {}),
    ...(status ? { status } : {}),
    ...(collector ? { collector } : {}),
  };
}

export type CollectorsSearchPatch = { [K in keyof CollectorsSearch]?: CollectorsSearch[K] | null };

/** @desc Merge a change into the search; `null` clears that key. */
export const withCollectorsSearch = (
  current: CollectorsSearch,
  patch: CollectorsSearchPatch,
): CollectorsSearch => parseCollectorsSearch({ ...current, ...patch });

/**
 * @desc The `automations.collector.list` input the filters ask for. Archived collectors are out
 * of default lists (spec "Archive keeps history"), so asking for them by status includes them.
 */
export const collectorsListInput = (
  search: CollectorsSearch,
): AutomationWire.ListCollectorsInput => ({
  limit: AutomationFilters.MAX_LIST,
  ...(search.kind ? { kind: search.kind } : {}),
  ...(search.status ? { status: search.status } : {}),
  ...(search.status === AutomationFilters.entityStatuses.Archived ? { includeArchived: true } : {}),
});
